import { Div, Text } from "atomize"
import { theme } from "./settings/config"
import { useContext, useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { profileContext } from "./providers/profile.provider"
import { schoolContext } from "./providers/school.provider"
import { sharedDataContext } from "./providers/sharedData.provider"
import { canEditLesson, getLessonColor } from "./utils/func.utils"
import { isCancelledLesson } from "./utils/cancellation.utils"
import LessonHeader from "./lesson/LessonHeader"
import LessonBody from "./lesson/LessonBody"
import LessonFooter from "./lesson/LessonFooter"
import LessonPresence from "./lesson/LessonPresence"
import LessonActions from "./lesson/LessonActions"
import LessonContent from "./lesson/LessonContent"
import LessonAuthorship from "./lesson/LessonAuthorship"
import CardInset from "./lesson/CardInset"
import Paragraph from "./atoms/Paragraph"
import SwipeActions from "./lesson/SwipeActions"
import LessonObjective from "./lesson/LessonObjective"
import { JustFinishedStub } from "./lesson/JustFinished"

function Lesson({
	lessonData,
	lessonCoach,
	isNextLessonConsecutive,
	isPrevLessonConsecutive,
	isOpen,
	handleOpenLessonActions,
	modalView,
	justFinishedLabel,
	overlapsWith,
}) {
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)

	const [isLayoutOpen, setIsLayoutOpen] = useState(false)

	const canEdit = canEditLesson(coach, selectedSchool, lessonData)
	const isCancelled = isCancelledLesson(lessonData)
	// l'ultima finita senza note ha il biglietto in fondo, anche aperta (vedi JustFinished)
	const hasStub = Boolean(justFinishedLabel) && !modalView

	const getRoundedValue = isDragging => {
		if (isDragging) return theme.rounded["20px"]
		if (!isNextLessonConsecutive) {
			if (!isPrevLessonConsecutive) return theme.rounded["20px"]
			else return theme.rounded["b-20px"]
		} else {
			if (!isPrevLessonConsecutive) return theme.rounded["t-20px"]
			else return theme.rounded["5px"]
		}
	}

	// Sincronizza con isOpen ma con delay in chiusura
	useEffect(() => {
		if (isOpen) {
			setIsLayoutOpen(true)
		} else {
			const timeout = setTimeout(() => setIsLayoutOpen(false), 50)
			return () => clearTimeout(timeout)
		}
	}, [isOpen])

	return (
		<SwipeActions lessonData={lessonData} canEdit={canEdit}>
			{isDragging => {
				const margins = {
					t: isPrevLessonConsecutive ? "0.25rem" : "1rem",
					b: isNextLessonConsecutive ? "0.25rem" : "1rem",
				}
				return (
					<Div
						bg={getLessonColor(getLessonType(lessonData.lessonType))}
						m={margins}
						// il biglietto arriva fino al bordo, e gli angoli lo tagliano
						p={{ t: "12px", b: hasStub ? "0" : "12px" }}
						rounded={getRoundedValue(isDragging)}
						transition
						shadow={theme.shadows.accent}
						style={{
							...(hasStub && { overflow: "hidden" }),
							// annullata: resta in elenco, ma si vede che non si fa
							...(isCancelled && { opacity: 0.55, filter: "grayscale(1)" }),
						}}>
						<Div
							p="6px 1rem"
							bg={getLessonColor(getLessonType(lessonData.lessonType))}
							onClick={() =>
								!modalView &&
								handleOpenLessonActions(
									`${lessonData.coachID}_${lessonData.lessonID}`,
								)
							}>
							<Div style={{ cursor: "pointer" }}>
								<LessonHeader lessonData={lessonData} overlapsWith={overlapsWith} />
								<LessonBody
									lessonData={lessonData}
									isOpen={isOpen}
									isLayoutOpen={isLayoutOpen}
									modalView={modalView}
								/>
								{!isOpen && !isCancelled && <LessonObjective lessonData={lessonData} />}
								<LessonFooter lessonData={lessonData} />
								{/* perché è saltata, anche a lezione chiusa */}
								{isCancelled && lessonData.lessonNotes && !isOpen && (
									<Text
										textSize="caption"
										textColor={theme.colors.gunmetal}
										m={{ t: "0.25rem" }}>
										{lessonData.lessonNotes}
									</Text>
								)}
							</Div>
						</Div>
						{!modalView && (
							<AnimatePresence>
								{isOpen && (
									<motion.div
										transition={{ ease: "easeInOut" }}
										initial={{ height: 0, y: -15 }}
										animate={{ height: "auto", y: 0 }}
										exit={{ height: 0, y: -15 }}
										style={{ overflow: "hidden" }}>
										{!isCancelled && <LessonPresence lessonData={lessonData} />}
										<LessonContent lessonData={lessonData} canEdit={canEdit} />
										{lessonData.lessonNotes && (
											<CardInset>
												<Paragraph m={{ t: "0.75rem" }} title="Note:">
													{lessonData.lessonNotes}
												</Paragraph>
											</CardInset>
										)}
										<LessonActions lessonData={lessonData} canEdit={canEdit} />
										<LessonAuthorship lessonData={lessonData} />
									</motion.div>
								)}
							</AnimatePresence>
						)}
						{hasStub && <JustFinishedStub lessonData={lessonData} label={justFinishedLabel} />}
					</Div>
				)
			}}
		</SwipeActions>
	)
}

export default Lesson
